// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

/**
 * ActivityFilters
 *
 * Shared filter strip used by both `ActivityLogPage` and `ClientActivityTab`.
 * Wraps the Action Type dropdown, Start/End date pickers and Clear button so
 * both feeds present an identical control row.
 */

import { Button, Dropdown, DateTimeInput } from '@loa/ui-components';
import type { ActivityLogFilters } from '../../types/activityLog';

export const TYPE_FILTER_OPTIONS = [
  { value: 'all', text: 'All actions' },
  { value: 'urgent', text: 'Urgent' },
  { value: 'warning', text: 'Warning' },
  { value: 'info', text: 'Info' },
  { value: 'insights', text: 'Insights' },
];

export type ActivityFiltersProps = Readonly<{
  filters: ActivityLogFilters;
  onChange: (key: keyof ActivityLogFilters, value: string | undefined) => void;
  onClear: () => void;
  disabled?: boolean;
}>;

export function ActivityFilters({ filters, onChange, onClear, disabled }: ActivityFiltersProps) {
  const hasActiveFilters =
    filters.typeFilter !== 'all' || !!filters.startDate || !!filters.endDate;

  return (
    <div className="activity-log__filters">
      <div className="activity-log__filter-field">
        <Dropdown
          label="Action Type"
          options={TYPE_FILTER_OPTIONS}
          value={filters.typeFilter}
          onChange={(val) => onChange('typeFilter', (val as ActivityLogFilters['typeFilter']) || 'all')}
          placeholder="All actions"
          disabled={disabled}
        />
      </div>

      <div className="activity-log__filter-field">
        <DateTimeInput
          label="Start Date"
          value={filters.startDate || ''}
          onChange={(val) => onChange('startDate', val || undefined)}
          disabled={disabled}
        />
      </div>

      <div className="activity-log__filter-field">
        <DateTimeInput
          label="End Date"
          value={filters.endDate || ''}
          onChange={(val) => onChange('endDate', val || undefined)}
          disabled={disabled}
        />
      </div>

      <div className="activity-log__filter-clear">
        <Button
          variant="outlined"
          colour="purple"
          size="lg"
          onClick={onClear}
          disabled={!hasActiveFilters || disabled}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}
